import React, { createContext, useContext, ReactNode } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";

// Tipo das features do salão (espelha SalonFeatures do backend)
export interface SalonFeaturesType {
  hasOnlineBooking: boolean;
  hasAiChatbot: boolean;
  hasOnlinePayment: boolean;
  hasVoiceNotes: boolean;
  hasAdvancedReports: boolean;
  hasCommissions: boolean;
  hasCheckout: boolean;
  hasPanelChamada: boolean;
  hasMediaPromocional: boolean;
  hasAiWhatsapp: boolean;
  hasAiInstagram: boolean;
  hasBranches: boolean;
  hasFinancial: boolean;
  hasCrm: boolean;
  hasInventory: boolean;
  hasOperations: boolean;
  hasIntelligence: boolean;
  hasAutomation: boolean;
  hasIntegrations: boolean;
  hasAcademy: boolean;
  hasSecurity: boolean;
  hasFiscal: boolean;
  overriddenBySuperAdmin: boolean;
}

// Defaults permissivos para superadmin (acesso total)
const SUPERADMIN_FEATURES: SalonFeaturesType = {
  hasOnlineBooking: true,
  hasAiChatbot: true,
  hasOnlinePayment: true,
  hasVoiceNotes: true,
  hasAdvancedReports: true,
  hasCommissions: true,
  hasCheckout: true,
  hasPanelChamada: true,
  hasMediaPromocional: true,
  hasAiWhatsapp: true,
  hasAiInstagram: true,
  hasBranches: true,
  hasFinancial: true,
  hasCrm: true,
  hasInventory: true,
  hasOperations: true,
  hasIntelligence: true,
  hasAutomation: true,
  hasIntegrations: true,
  hasAcademy: true,
  hasSecurity: true,
  hasFiscal: true,
  overriddenBySuperAdmin: false,
};

// Defaults básicos enquanto carrega
const DEFAULT_FEATURES: SalonFeaturesType = {
  hasOnlineBooking: true,
  hasAiChatbot: false,
  hasOnlinePayment: false,
  hasVoiceNotes: false,
  hasAdvancedReports: false,
  hasCommissions: false,
  hasCheckout: true,
  hasPanelChamada: false,
  hasMediaPromocional: false,
  hasAiWhatsapp: false,
  hasAiInstagram: false,
  hasBranches: false,
  hasFinancial: false,
  hasCrm: false,
  hasInventory: false,
  hasOperations: false,
  hasIntelligence: false,
  hasAutomation: false,
  hasIntegrations: false,
  hasAcademy: false,
  hasSecurity: false,
  hasFiscal: false,
  overriddenBySuperAdmin: false,
};

interface FeaturesContextValue {
  features: SalonFeaturesType;
  isLoading: boolean;
  hasFeature: (key: keyof SalonFeaturesType) => boolean;
}

const FeaturesContext = createContext<FeaturesContextValue>({
  features: DEFAULT_FEATURES,
  isLoading: false,
  hasFeature: () => false,
});

export function FeaturesProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "superadmin";

  const { data: features, isLoading } = trpc.salon.getMyFeatures.useQuery(undefined, {
    // Administradores e membros da equipe usam a mesma assinatura do salão.
    // Não restringir a consulta ao role global "admin", pois administradores
    // internos autenticados por usuário/senha possuem role global "user".
    enabled: !!user && !isSuperAdmin,
    staleTime: 60_000, // cache por 1 minuto
  });

  const resolvedFeatures: SalonFeaturesType = isSuperAdmin
    ? SUPERADMIN_FEATURES
    : features
    ? {
        hasOnlineBooking: features.hasOnlineBooking ?? true,
        hasAiChatbot: features.hasAiChatbot ?? false,
        hasOnlinePayment: features.hasOnlinePayment ?? false,
        hasVoiceNotes: features.hasVoiceNotes ?? false,
        hasAdvancedReports: features.hasAdvancedReports ?? false,
        hasCommissions: features.hasCommissions ?? false,
        hasCheckout: features.hasCheckout ?? true,
        hasPanelChamada: features.hasPanelChamada ?? false,
        hasMediaPromocional: features.hasMediaPromocional ?? false,
        hasAiWhatsapp: features.hasAiWhatsapp ?? false,
        hasAiInstagram: features.hasAiInstagram ?? false,
        hasBranches: (features as any).hasBranches ?? false,
        hasFinancial: (features as any).hasFinancial ?? false,
        hasCrm: (features as any).hasCrm ?? false,
        hasInventory: (features as any).hasInventory ?? false,
        hasOperations: (features as any).hasOperations ?? false,
        hasIntelligence: (features as any).hasIntelligence ?? false,
        hasAutomation: (features as any).hasAutomation ?? false,
        hasIntegrations: (features as any).hasIntegrations ?? false,
        hasAcademy: (features as any).hasAcademy ?? false,
        hasSecurity: (features as any).hasSecurity ?? false,
        hasFiscal: (features as any).hasFiscal ?? false,
        overriddenBySuperAdmin: features.overriddenBySuperAdmin ?? false,
      }
    : DEFAULT_FEATURES;

  const hasFeature = (key: keyof SalonFeaturesType): boolean => {
    return Boolean(resolvedFeatures[key]);
  };

  return (
    <FeaturesContext.Provider value={{ features: resolvedFeatures, isLoading, hasFeature }}>
      {children}
    </FeaturesContext.Provider>
  );
}

export function useFeatures() {
  return useContext(FeaturesContext);
}
